import { useCloned } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { ref } from 'vue';

import type { BoundingBox } from '@/types/mode-map';

import { useFiltersStore } from '@/stores/useFiltersStore';
import { useMetricsStore } from '@/stores/useMetricsStore';
import { useProductsStore } from '@/stores/useProductsStore';

export default function useModeMap() {
  const filtersStore = useFiltersStore();
  const metricsStore = useMetricsStore();
  const productsStore = useProductsStore();

  const { filters, datesAsStrings } = storeToRefs(filtersStore);
  const { places, themes, types, taxonomyFilter } = storeToRefs(metricsStore);
  const { products, totalResults, nextPaginationKey } = storeToRefs(productsStore);

  const map = ref();
  const initialState = ref();
  const isLoadingMapMovement = ref(false);
  const lastValidBoundingBox = ref<{ center: [number, number]; range: [number, number] } | null>(null);

  function mountMap() {
    initialState.value = {
      filters: useCloned(filters).cloned.value,
      products: useCloned(products).cloned.value,
      totalResults: totalResults.value,
      nextPaginationKey: nextPaginationKey.value,
      places: useCloned(places).cloned.value,
      themes: useCloned(themes).cloned.value,
      types: useCloned(types).cloned.value,
      datesAsStrings: datesAsStrings.value,
      taxonomyFilter: useCloned(taxonomyFilter).cloned.value,
    };

    document.body.style.overflow = 'hidden';
    filters.value.size = 9999;
    filtersStore.saveToStorage(filters.value);
  }

  function unMountMap() {
    if (!initialState.value) {
      console.warn('No initial state found, cannot restore');
      return;
    }

    if (map.value) {
      map.value.removeAllListeners?.();
      map.value = null;
    }

    resetFilters();
    document.body.style.overflow = '';
  }

  function resetFilters() {
    filters.value = initialState.value.filters;
    products.value = initialState.value.products;
    totalResults.value = initialState.value.totalResults;
    nextPaginationKey.value = initialState.value.nextPaginationKey;
    places.value = initialState.value.places;
    themes.value = initialState.value.themes;
    types.value = initialState.value.types;
    datesAsStrings.value = initialState.value.datesAsStrings;
    taxonomyFilter.value = initialState.value.taxonomyFilter;

    document.body.style.overflow = '';
    filtersStore.saveToStorage(filters.value);
  }

  function createCloseMapButton(emit: any, isMobile: boolean, text: string) {
    const closeMapSymbol = Symbol('close-map-button');
    const closePanel = document.createElement('div');
    closePanel.classList.add('maplibregl-ctrl');

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.setAttribute('aria-label', 'Fermer la carte');

    const crossSVG = `
      <svg width="25" height="25" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g id="Icon">
          <path id="Vector" d="M14.6666 5.33301L5.33325 14.6663M5.33325 5.33301L14.6666 14.6663" stroke="white" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
        </g>
      </svg>
    `;

    closeButton.innerHTML = isMobile ? crossSVG : `${crossSVG}<span>${text}</span>`;

    closeButton.style.cssText = `
      display: flex;
      justify-content: center;
      color: var(--yb-color-white);
      background-color: var(--yb-color-primary);
      border: none;
      border-radius: var(--yb-radius-full);
      font-size: var(--yb-text-base);
      font-weight: var(--yb-text-semi-bold);
      line-height: var(--yb-text-lineheight);
      cursor: pointer;
      padding: ${isMobile ? 'var(--yb-spacing-xs)' : 'var(--yb-spacing-xs) var(--yb-spacing-sm)'};
      align-items: center;
      gap: var(--yb-spacing-xs);
    `;

    closeButton.addEventListener('click', () => {
      emit('close');
    });

    closePanel.appendChild(closeButton);
    return { symbol: closeMapSymbol, element: closePanel };
  }

  function createSliderToggleButton(showProductSlider: any) {
    const closeSliderSymbol = Symbol('close-slider-button');
    const closeSliderPanel = document.createElement('div');
    closeSliderPanel.classList.add('maplibregl-ctrl', 'maplibregl-ctrl-group');

    const closeSliderButton = document.createElement('button');
    closeSliderButton.type = 'button';
    closeSliderButton.setAttribute('aria-label', 'Masquer le slider');

    const leftChevronSVG = `
      <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5.6667 10.6663L1 5.99968L5.6667 1.33301" stroke="black" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;

    const rightChevronSVG = `
      <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 10.6663L5.6667 5.99968L1 1.33301" stroke="black" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;

    closeSliderButton.innerHTML = showProductSlider.value ? leftChevronSVG : rightChevronSVG;

    closeSliderButton.style.cssText = `
      font-size: var(--yb-text-base);
      font-weight: var(--yb-text-semi-bold);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
    `;

    closeSliderButton.addEventListener('click', () => {
      showProductSlider.value = !showProductSlider.value;
      closeSliderButton.innerHTML = showProductSlider.value ? leftChevronSVG : rightChevronSVG;
    });

    closeSliderPanel.appendChild(closeSliderButton);
    return { symbol: closeSliderSymbol, element: closeSliderPanel };
  }

  function getBoundingBox(center: [number, number], range: [number, number]): BoundingBox {
    return {
      // Assuming center is [longitude, latitude] and range is [longitudeRange, latitudeRange]
      topLeftLat: center[1] + range[1],
      topLeftLon: center[0] - range[0],
      bottomRightLat: center[1] - range[1],
      bottomRightLon: center[0] + range[0],
    };
  }

  return {
    map,
    createCloseMapButton,
    createSliderToggleButton,
    mountMap,
    unMountMap,
    getBoundingBox,
    isLoadingMapMovement,
    lastValidBoundingBox,
  };
}
